/**
 * WaiverSignPanel - Inline sign flow for a single unsigned waiver on the member portal terms page.
 */

"use client";

import { Box, Flex, FormControl, FormLabel } from "@chakra-ui/react";
import { useCallback, useEffect, useState, type MutableRefObject } from "react";
import type { GetPolicyResponse } from "@/openapi/generated";
import { Checkbox } from "@/components";
import { useToast } from "@/components/feedback";
import { Badge, Button, Text } from "@/components/ui";
import { usePolicyAccept } from "@/hooks";
import { useThemeTokens } from "@/lib/theme/tokens";

interface WaiverSignPanelProps {
  waiver: GetPolicyResponse;
  userEmail: string;
  textRefs: MutableRefObject<Record<number, HTMLDivElement | null>>;
  waiverIndex: number;
}

export const WaiverSignPanel = ({
  waiver,
  userEmail,
  textRefs,
  waiverIndex,
}: WaiverSignPanelProps) => {
  const theme = useThemeTokens();
  const toast = useToast();
  const { mutate, isPending } = usePolicyAccept(toast);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);

  useEffect(() => {
    setHasScrolledToBottom(false);
    setIsAgreed(false);
  }, [waiver.policyId]);

  useEffect(() => {
    const container = textRefs.current[waiverIndex];
    if (!container) {
      setHasScrolledToBottom(false);
      return;
    }

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= 10;
      if (isAtBottom || scrollHeight <= clientHeight + 10) {
        setHasScrolledToBottom(true);
      }
    };

    const initialTimer = setTimeout(checkScroll, 100);
    container.addEventListener("scroll", checkScroll);

    return () => {
      clearTimeout(initialTimer);
      container.removeEventListener("scroll", checkScroll);
    };
  }, [textRefs, waiverIndex, waiver.policyId]);

  const canAgree = hasScrolledToBottom;

  const handleSign = useCallback(() => {
    if (!canAgree || !isAgreed || waiver.policyId == null) return;

    mutate({
      policyId: Number(waiver.policyId),
      userEmail,
      policyName: waiver.name,
      policyDisplayText: waiver.displayText ?? null,
    });
  }, [canAgree, isAgreed, mutate, userEmail, waiver]);

  return (
    <Box
      mt={4}
      pt={4}
      borderTopWidth="1px"
      borderColor={theme.borderGlass.default}
    >
      <Badge tone="info" fontSize="xs" mb={3}>
        Action required
      </Badge>

      {!hasScrolledToBottom ? (
        <Text variant="secondary" fontSize="sm" mb={3}>
          Scroll through the full waiver text above to enable signing.
        </Text>
      ) : null}

      <FormControl>
        <Flex columnGap="5px" align="flex-start" mb={3}>
          <Checkbox
            mt="1px"
            id={`sign-waiver-${waiver.policyId}`}
            isChecked={isAgreed}
            onChange={(event) => setIsAgreed(event.target.checked)}
            isDisabled={!canAgree}
          />
          <FormLabel
            htmlFor={`sign-waiver-${waiver.policyId}`}
            mb="0"
            cursor={canAgree ? "pointer" : "not-allowed"}
            fontSize="sm"
          >
            <Text variant="primary" fontSize="sm">
              I have read and agree to this waiver
            </Text>
          </FormLabel>
        </Flex>
      </FormControl>

      <Button
        variant="primary"
        size="sm"
        onClick={handleSign}
        isLoading={isPending}
        isDisabled={!canAgree || !isAgreed}
      >
        Sign waiver
      </Button>
    </Box>
  );
};
